// frontend/src/pages/portfolio/components/PortfolioHeader.tsx
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock, Download, Menu, X } from "lucide-react";
import { getMicrosoftStoreUrl } from "../../../utils/storeUrl";
import { navLinkClass } from "./styles";

const PortfolioHeader: React.FC = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    if (!mobileMenuOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMobileMenuOpen(false);
    };
    document.addEventListener("keydown", handleKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = "";
    };
  }, [mobileMenuOpen]);

  const close = () => setMobileMenuOpen(false);

  return (
    <header className="sticky top-0 z-50 border-b border-primary-200/40 bg-slate-50/80 backdrop-blur-md">
      <div className="mx-auto max-w-6xl px-4 py-3 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between gap-4">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-primary-900 text-white shadow-elevated ring-1 ring-primary-800/40">
              <Clock className="h-5 w-5" strokeWidth={2} aria-hidden />
            </div>
            <div className="min-w-0">
              <p className="font-display text-lg font-semibold tracking-tight text-primary-900 truncate">
                Time Tracker
              </p>
              <p className="hidden text-xs text-primary-700 sm:block">
                by Synquar Ai Labs
              </p>
            </div>
          </div>

          <nav className="hidden items-center gap-1 md:flex" aria-label="Page sections">
            <a href="#pricing" className={navLinkClass}>Pricing</a>
            <a href="#how-it-works" className={navLinkClass}>How it works</a>
            <a href="#roles" className={navLinkClass}>Roles</a>
            <a href="#faq" className={navLinkClass}>FAQ</a>
            <Link to="/getting-started" className={navLinkClass}>Guide</Link>
            <a href="#contact" className={navLinkClass}>Contact</a>
          </nav>

          <div className="flex shrink-0 items-center gap-2">
            <a
              href={getMicrosoftStoreUrl()}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Get Time Tracker on the Microsoft Store"
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-primary-200 bg-white px-3 py-2.5 sm:px-4 text-sm font-semibold text-primary-900 shadow-xs hover:bg-primary-50 hover:border-primary-300 transition-colors min-h-[44px] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 touch-manipulation"
            >
              <Download className="h-4 w-4" aria-hidden />
              <span className="hidden sm:inline">Get app</span>
              <span className="sm:hidden">Get</span>
            </a>
            <Link
              to="/login"
              className="hidden md:inline-flex group btn-primary items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold shadow-elevated min-h-[44px] min-w-[44px] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 touch-manipulation"
            >
              Log in
              <ArrowRight className="h-4 w-4 motion-safe:transition-transform motion-safe:group-hover:translate-x-0.5" aria-hidden />
            </Link>
            <button
              type="button"
              onClick={() => setMobileMenuOpen((v) => !v)}
              aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileMenuOpen}
              aria-controls="portfolio-mobile-menu"
              className="md:hidden inline-flex h-11 w-11 items-center justify-center rounded-xl border border-primary-200 bg-white text-primary-900 hover:bg-primary-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
            >
              {mobileMenuOpen ? <X className="h-5 w-5" aria-hidden /> : <Menu className="h-5 w-5" aria-hidden />}
            </button>
          </div>
        </div>
      </div>

      {mobileMenuOpen && (
        <>
          <div
            className="fixed inset-0 top-[64px] z-40 bg-gray-900/30 backdrop-blur-[2px] md:hidden"
            onClick={close}
            aria-hidden
          />
          <nav
            id="portfolio-mobile-menu"
            className="absolute left-0 right-0 z-50 border-b border-primary-200/40 bg-slate-50/95 backdrop-blur-md shadow-elevated md:hidden animate-slide-up"
            aria-label="Page sections"
          >
            <div className="mx-auto flex max-w-6xl flex-col gap-1 px-4 py-3 sm:px-6">
              <a href="#pricing" onClick={close} className={navLinkClass}>Pricing</a>
              <a href="#how-it-works" onClick={close} className={navLinkClass}>How it works</a>
              <a href="#roles" onClick={close} className={navLinkClass}>Roles</a>
              <a href="#faq" onClick={close} className={navLinkClass}>FAQ</a>
              <Link to="/getting-started" onClick={close} className={navLinkClass}>Guide</Link>
              <a href="#contact" onClick={close} className={navLinkClass}>Contact</a>
              <Link
                to="/login"
                onClick={close}
                className="mt-2 group btn-primary inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold shadow-elevated min-h-[44px]"
              >
                Log in
                <ArrowRight className="h-4 w-4" aria-hidden />
              </Link>
            </div>
          </nav>
        </>
      )}
    </header>
  );
};

export default PortfolioHeader;
